import { Box, CircularProgress, Grid, useMediaQuery } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SadAvatar from "../../components/shared/sadAvatar";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import IconButton from "@mui/material/IconButton";
import { useHistory, useLocation } from "react-router-dom";
import ReportsSearch from "./search";
import { useDispatch, useSelector } from "react-redux";
import {
  downloadEvidencePack,
  getListOfAllReports,
  requestEvidencePack,
} from "../../redux/actions/reportsActionCreator";
import {
  getOrganizatonMetricsById,
  setOrganizationId,
} from "../../redux/actions/organizationProfileCreators";
import RapportCard from "./rapportCard";
import PdfView from "./pdfView";
import ReportActions from "./reportActions";

const normalizeOrgaId = (value) => {
  const normalized =
    value !== undefined && value !== null ? String(value).trim() : "";
  if (
    !normalized ||
    normalized.toLowerCase() === "null" ||
    normalized.toLowerCase() === "undefined"
  ) {
    return null;
  }
  return normalized;
};

const getReportId = (report) =>
  report?.report_id || report?.id || report?._id || report?.uuid || report?.url;

const getReportTitle = (report) => {
  const reportType = (report?.report_type || "").toLowerCase();
  const isFlashReport = reportType === "flash_report";
  if (isFlashReport) return "Flash Report";

  const validationStatus =
    typeof report?.validation_status === "boolean" ? report.validation_status : true;
  return validationStatus ? "Risk Report With validation" : "Risk Report ( Automatic)";
};

const getEvidenceStatusFromReport = (report) => {
  const status = String(report?.evidence_pack?.status || "").toLowerCase();
  if (status === "in_progress") return "processing";
  if (status === "completed" && report?.evidence_pack?.zip_available) {
    return "completed";
  }
  if (status === "failed" || status === "failure") return "failed";
  return "idle";
};

const getSuggestedFileName = (url) => {
  try {
    const clean = url.split("?")[0].split("#")[0];
    const base = clean.substring(clean.lastIndexOf("/") + 1) || "document.pdf";
    return base.endsWith(".pdf") ? base : `${base}.pdf`;
  } catch {
    return "document.pdf";
  }
};

const openDownloadUrl = (url, filename) => {
  const a = document.createElement("a");
  a.href = url;
  if (filename) {
    a.download = filename;
  }
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

function Report() {
  const isMobile = useMediaQuery("(max-width:900px)");
  const [searchText, setSearchText] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [evidencePackStatus, setEvidencePackStatus] = useState({});
  const [evidencePackDownloading, setEvidencePackDownloading] = useState({});
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const storeOrgaId = useSelector((state) => state.organizationProfile.id);
  const notifications = useSelector((state) => state.notifications.all || []);
  const localOrgaId = localStorage.getItem("orgaId");
  const queryOrgaId = new URLSearchParams(location.search).get("orga_id");

  const storageOrgaId =
    normalizeOrgaId(storeOrgaId) || normalizeOrgaId(localOrgaId);
  const normalizedQueryOrgaId = normalizeOrgaId(queryOrgaId);
  // A notification/deep-link explicitly names the report owner. It must win on
  // the first render, before InsideLayout has synchronized Redux/localStorage,
  // or this page requests the previously selected organization's reports.
  const resolvedOrgaId = normalizedQueryOrgaId || storageOrgaId;
  const reports = useSelector((state) => state.reports.all || []);
  const previousNotificationSignal = useRef("");
  const previousIsMobile = useRef(isMobile);
  const notificationSignal = useMemo(
    () =>
      notifications
        .map(
          (item) =>
            item?.id ||
            item?._id ||
            item?.created_at ||
            item?.createdAt ||
            item?.message ||
            ""
        )
        .join("|"),
    [notifications]
  );
  const visibleReports = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return reports;
    return reports.filter((report) =>
      getReportTitle(report).toLowerCase().includes(query)
    );
  }, [reports, searchText]);

  const processingReportIds = useMemo(
    () =>
      Object.entries(evidencePackStatus)
        .filter(([, status]) => status === "processing")
        .map(([id]) => id),
    [evidencePackStatus]
  );

  const getEpStatus = useCallback(
    (report) => {
      const reportId = getReportId(report);
      return evidencePackStatus[reportId] || getEvidenceStatusFromReport(report);
    },
    [evidencePackStatus]
  );

  const goToDashbord = () => {
    history.push("/enterprise");
  };

  const handleDownloadPdf = async (report) => {
    if (!report?.url) return;
    const filename = getSuggestedFileName(report.url);

    try {
      setDownloading(true);
      const res = await fetch(report.url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch PDF");
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      openDownloadUrl(blobUrl, filename);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      window.open(report.url, "_blank", "noopener,noreferrer");
    } finally {
      setDownloading(false);
    }
  };

  const handleRequestEvidencePack = async (report) => {
    const reportId = report?.report_id;
    const key = getReportId(report);
    if (!resolvedOrgaId || !reportId || !key) return;

    setEvidencePackStatus((prev) => ({
      ...prev,
      [key]: "processing",
    }));

    try {
      const res = await dispatch(requestEvidencePack(resolvedOrgaId, reportId));
      const nextStatus =
        res?.status === "completed"
          ? "completed"
          : res?.status === "in_progress"
          ? "processing"
          : getEvidenceStatusFromReport({ evidence_pack: res?.evidence_pack });

      setEvidencePackStatus((prev) => ({
        ...prev,
        [key]: nextStatus || "processing",
      }));

      if (res?.status === "completed") {
        dispatch(getListOfAllReports(resolvedOrgaId, false));
      }
    } catch (e) {
      setEvidencePackStatus((prev) => ({
        ...prev,
        [key]: "failed",
      }));
    }
  };

  const handleDownloadEvidencePack = async (report) => {
    const reportId = report?.report_id;
    const key = getReportId(report);
    if (!resolvedOrgaId || !reportId || !key) return;

    setEvidencePackDownloading((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await dispatch(downloadEvidencePack(resolvedOrgaId, reportId));
      if (res?.download_url) {
        // This is a real attachment navigation, so Safari, Firefox, Chrome,
        // and Edge all use their native download handling.
        window.location.assign(res.download_url);
      }
    } finally {
      setEvidencePackDownloading((prev) => ({ ...prev, [key]: false }));
    }
  };

  useEffect(() => {
    if (isMobile && !previousIsMobile.current) {
      setSelectedReport(null);
    }
    previousIsMobile.current = isMobile;
  }, [isMobile]);

  useEffect(() => {
    if (normalizedQueryOrgaId && normalizedQueryOrgaId !== storageOrgaId) {
      localStorage.setItem("orgaId", normalizedQueryOrgaId);
      dispatch(setOrganizationId(normalizedQueryOrgaId));
      dispatch(getOrganizatonMetricsById(normalizedQueryOrgaId));
    }
  }, [dispatch, normalizedQueryOrgaId, storageOrgaId]);

  useEffect(() => {
    let isCurrent = true;
    setSelectedReport(null);
    setReportsLoading(true);

    Promise.resolve(dispatch(getListOfAllReports(resolvedOrgaId, false))).finally(
      () => {
        if (isCurrent) setReportsLoading(false);
      }
    );

    return () => {
      isCurrent = false;
    };
  }, [dispatch, resolvedOrgaId]);

  useEffect(() => {
    setEvidencePackStatus((prev) => {
      let changed = false;
      const next = { ...prev };

      reports.forEach((report) => {
        const id = getReportId(report);
        if (!id || !next[id]) return;
        const serverStatus = getEvidenceStatusFromReport(report);
        if (serverStatus !== "idle" && next[id] !== serverStatus) {
          next[id] = serverStatus;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [reports]);

  useEffect(() => {
    if (!isMobile) return;
    const shouldHide = Boolean(selectedReport);
    const current = Boolean(location?.state?.hideOrgaSelector);
    if (current === shouldHide) return;

    history.replace({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: { ...(location.state || {}), hideOrgaSelector: shouldHide },
    });
  }, [isMobile, selectedReport, history, location]);

  useEffect(() => {
    if (isMobile || reportsLoading) return;

    if (visibleReports.length === 0) {
      if (selectedReport) setSelectedReport(null);
      return;
    }

    const selectedId = getReportId(selectedReport);
    const freshSelectedReport =
      selectedId &&
      visibleReports.find((report) => getReportId(report) === selectedId);

    if (freshSelectedReport && freshSelectedReport !== selectedReport) {
      setSelectedReport(freshSelectedReport);
      return;
    }

    if (!freshSelectedReport) {
      setSelectedReport(visibleReports[0] || null);
    }
  }, [isMobile, visibleReports, reportsLoading, selectedReport]);

  useEffect(() => {
    if (!resolvedOrgaId || processingReportIds.length === 0) {
      previousNotificationSignal.current = notificationSignal;
      return;
    }

    if (
      notificationSignal &&
      previousNotificationSignal.current &&
      notificationSignal !== previousNotificationSignal.current
    ) {
      dispatch(getListOfAllReports(resolvedOrgaId, false));
    }

    previousNotificationSignal.current = notificationSignal;
  }, [dispatch, notificationSignal, processingReportIds.length, resolvedOrgaId]);

  return (
    <div>
      <Grid container>
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            display: {
              xs: selectedReport ? "none" : "block",
              md: "block",
            },
          }}
        >
          <Box
            sx={{
              padding: !isMobile ? "50px 20px" : "10px",
              borderRight: !isMobile
                ? "1px solid rgba(0, 0, 0, 0.1)"
                : "none",
              height: !isMobile ? "calc(100vh - 100px)" : "auto",
              overflow: !isMobile ? "auto" : "visible",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <IconButton
                onClick={goToDashbord}
                aria-label={t("back_to_dashboard")}
              >
                <KeyboardBackspaceIcon />
              </IconButton>
              <span
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "22px",
                  fontWeight: "600",
                  padding: "0px 16px",
                }}
              >
                {t("back_to_dashboard")}
              </span>
            </div>

            <ReportsSearch
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />

            <Box sx={{ marginTop: "20px" }}>
              {reportsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "160px",
                  }}
                >
                  <CircularProgress size={36} />
                </Box>
              ) : (
                visibleReports.map((report, idx) => (
                  <RapportCard
                    key={getReportId(report) || report?.title || idx}
                    item={report}
                    selected={getReportId(report) === getReportId(selectedReport)}
                    onClick={() => setSelectedReport(report)}
                  />
                ))
              )}
            </Box>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md={8}
          sx={{
            display: {
              xs: selectedReport ? "block" : "none",
              md: "block",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: !isMobile ? "calc(100vh - 100px)" : "100vh",
              overflow: "hidden",
            }}
          >
            {isMobile && selectedReport && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1,
                  py: 1,
                  minHeight: 58,
                  borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    onClick={() => setSelectedReport(null)}
                    aria-label={t("back")}
                  >
                    <KeyboardBackspaceIcon />
                  </IconButton>
                  <span
                    style={{
                      fontFamily: "Barlow Condensed",
                      fontSize: "20px",
                      fontWeight: 600,
                      paddingLeft: 8,
                    }}
                  >
                    {t("back_to_reports") || t("back")}
                  </span>
                </Box>

                <ReportActions
                  item={selectedReport}
                  isMobile
                  downloading={downloading}
                  evidencePackDownloading={
                    evidencePackDownloading[getReportId(selectedReport)]
                  }
                  evidencePackStatus={getEpStatus(selectedReport)}
                  onDownloadPdf={() => handleDownloadPdf(selectedReport)}
                  onRequestEvidencePack={() =>
                    handleRequestEvidencePack(selectedReport)
                  }
                  onDownloadEvidencePack={() =>
                    handleDownloadEvidencePack(selectedReport)
                  }
                />
              </Box>
            )}

            {!isMobile && !reportsLoading && selectedReport && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  px: 2,
                  py: 1.5,
                  minHeight: 62,
                  borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                  flexShrink: 0,
                }}
              >
                <ReportActions
                  item={selectedReport}
                  isMobile={false}
                  downloading={downloading}
                  evidencePackDownloading={
                    evidencePackDownloading[getReportId(selectedReport)]
                  }
                  evidencePackStatus={getEpStatus(selectedReport)}
                  onDownloadPdf={() => handleDownloadPdf(selectedReport)}
                  onRequestEvidencePack={() =>
                    handleRequestEvidencePack(selectedReport)
                  }
                  onDownloadEvidencePack={() =>
                    handleDownloadEvidencePack(selectedReport)
                  }
                />
              </Box>
            )}

            <Box
              sx={{
                flex: 1,
                width: "100%",
                minHeight: 0,
                overflow: isMobile ? "auto" : "visible",
                display: "flex",
                alignItems: "stretch",
              }}
            >
              {reportsLoading ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "240px",
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
              ) : selectedReport ? (
                <Box sx={{ flex: 1, width: "100%" }}>
                  <PdfView item={selectedReport} />
                </Box>
              ) : (
                <Box sx={{ flex: 1, width: "100%" }}>
                  <SadAvatar label={t("no_selected_report")} alt="NO REPORTS" />
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </div>
  );
}

export default Report;
